/**
 * useScorerLock — single-writer concurrency for live scoring.
 *
 * Acquires a server-side lock on mount. Sends a heartbeat every 60s to keep
 * it alive. Releases on unmount / page-hide. If another scorer holds it,
 * exposes that fact so the BBB page can switch to read-only mode.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import api from "../api/axios";

const HEARTBEAT_MS = 60_000;

export function useScorerLock(matchId) {
  const [state, setState] = useState({
    status: "idle",        // idle | acquiring | held | locked-by-other | error
    holder: null,           // {holder_id, holder_username, expires_at}
    isSelf: true,           // optimistic until first response
    error: "",
  });
  const timerRef = useRef(null);
  const releasedRef = useRef(false);

  const acquire = useCallback(async () => {
    if (!matchId) return;
    setState((s) => ({ ...s, status: "acquiring", error: "" }));
    try {
      const r = await api.post("/api/scoring/lock/acquire/", { match_id: matchId });
      releasedRef.current = false;
      setState({
        status: "held",
        holder: r.data?.lock || null,
        isSelf: true,
        error: "",
      });
    } catch (e) {
      if (e?.response?.status === 423) {
        const lock = e.response.data?.lock || null;
        setState({
          status: "locked-by-other",
          holder: lock,
          isSelf: false,
          error: "",
        });
      } else {
        setState((s) => ({ ...s, status: "error", error: e?.message || "acquire failed" }));
      }
    }
  }, [matchId]);

  const heartbeat = useCallback(async () => {
    if (!matchId || releasedRef.current) return;
    try {
      const r = await api.post("/api/scoring/lock/heartbeat/", { match_id: matchId });
      setState((s) => ({ ...s, status: "held", holder: r.data?.lock || s.holder, isSelf: true }));
    } catch (e) {
      // Lost the lock — try to re-acquire transparently
      if (e?.response?.status === 423) {
        const lock = e.response.data?.lock || null;
        setState({ status: "locked-by-other", holder: lock, isSelf: false, error: "" });
      }
    }
  }, [matchId]);

  const release = useCallback(async () => {
    if (!matchId || releasedRef.current) return;
    releasedRef.current = true;
    try {
      await api.post("/api/scoring/lock/release/", { match_id: matchId });
    } catch { /* best effort */ }
  }, [matchId]);

  const takeover = useCallback(async () => {
    if (!matchId) return;
    try {
      const r = await api.post("/api/scoring/lock/takeover/", { match_id: matchId });
      setState({ status: "held", holder: r.data?.lock || null, isSelf: true, error: "" });
    } catch (e) {
      setState((s) => ({ ...s, error: e?.response?.data?.error || "takeover failed" }));
    }
  }, [matchId]);

  // Acquire on mount + heartbeat loop + release on unmount
  useEffect(() => {
    if (!matchId) return;
    acquire();
    timerRef.current = setInterval(heartbeat, HEARTBEAT_MS);

    // Release when tab is hidden / page unloads
    const onHide = () => { release(); };
    window.addEventListener("beforeunload", onHide);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") release();
    });

    return () => {
      clearInterval(timerRef.current);
      window.removeEventListener("beforeunload", onHide);
      release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  return {
    ...state,
    canScore: state.status === "held",
    reacquire: acquire,
    forceTakeover: takeover,
  };
}
